import { differenceInDays, format, parseISO } from 'date-fns';
import { TASK_TYPE_ID_MAP, TASK_TYPES } from 'src/constants/task-types';

export const isTaskCurrent = (task) => {
  let current = false;
  try {
    if (new Date(task?.end_date) > new Date()) {
      current = true;
    }
  } catch (error) {}
  return current;
};

export const isTaskActive = (task) => {
  return !task?.is_archived;
};

/**
 * Hasura conditions for a task people can still be assigned to: not archived, not past its end
 * date, in an active campaign (status 1), and not a bulk-task parent. The first three are the same
 * rule as the active_tasks CTE in backend/express/services/near-me.ts and the app's volunteer form;
 * keep them in step.
 *
 * A bulk parent is never assigned, only its children. is_bulk_task does not mark it (it is set on
 * children, and not on all of them); a parent has an enabled split config, or children, or both.
 * The config test is spelled null-safe: `_not` of `_contains` on a NULL config is NULL, which
 * would drop every ordinary task.
 */
export const assignableTaskConditions = (today = format(new Date(), 'yyyy-MM-dd')) => [
  { _or: [{ is_archived: { _eq: false } }, { is_archived: { _is_null: true } }] },
  { _or: [{ end_date: { _is_null: true } }, { end_date: { _gte: today } }] },
  { tasks_task_campaigns: { task_campaigns_campaign: { status_id: { _eq: 1 } } } },
  {
    _or: [
      { bulk_task_split_config: { _is_null: true } },
      { _not: { bulk_task_split_config: { _contains: { enabled: true } } } },
    ],
  },
  { _not: { tasks_sub_tasks: {} } },
];

export const isTaskExpiringSoon = (task) => {
  let expiringSoon = false;
  try {
    const startDate = task?.end_date;
    const parsedDate = parseISO(startDate, 'YYYY-MM-DD');
    const diff = differenceInDays(parsedDate, new Date());
    expiringSoon = diff <= 15;
  } catch (error) {
    console.log(error);
  }

  return expiringSoon;
};

export const replaceFileExtension = (name, ext) => {
  const arr = name.split('.');
  arr.splice(-1, 1);
  arr.push(ext);
  return arr.join('.');
};

const MATCHABLE_FIELDS = [
  'state_id',
  'county_id',
  'city_id',
  'city_council_id',
  'precinct_id',
  'state_leg_lower_district_id',
  'state_leg_upper_district_id',
  'congressional_district_id',
  'ward',
  'jurisname',
  'school_district',
  'school_board',
  'advisory_neighborhood_district',
  'county_council',
  'county_council_district',
  'county_commissioner',
  'county_commissioner_district',
  'justice_of_the_peace_district',
];

export const makeVotingAreaKey = (area = {}) => {
  const parts = MATCHABLE_FIELDS.map((field) => area?.[field]).filter(Boolean);
  return parts.join('-') || `area-${area?.voting_area_id || 'unknown'}`;
};

// Upload-numbers texting tasks have no voters_queries/sms_histories; their counts
// live in the linked manual_text_batch aggregates exposed on TaskFragment.
export const getManualTextBatch = (task) =>
  task?.is_manual_number_task ? task?.tasks_manual_text_batches?.[0] : null;

export const getManualTextTotal = (task) =>
  getManualTextBatch(task)?.total_count?.aggregate?.count ?? 0;

export const getManualTextSent = (task) =>
  getManualTextBatch(task)?.sent_count?.aggregate?.count ?? 0;

export const getManualTextSending = (task) =>
  getManualTextBatch(task)?.sending_count?.aggregate?.count ?? 0;

// Mirrors the automated-task "failed" bucket: everything that is neither sent nor still sending.
// Canceled rows fall in here as well as in getManualTextCanceled, matching how the sms_history
// based counts on the task status screen overlap.
export const getManualTextFailed = (task) =>
  getManualTextBatch(task)?.failed_count?.aggregate?.count ?? 0;

export const getManualTextCanceled = (task) =>
  getManualTextBatch(task)?.canceled_count?.aggregate?.count ?? 0;

// "Completed" mirrors FINISHED_SMS_STATUSES semantics used for automated MESSAGE
// tasks: every message that has reached a terminal state (sent/delivered OR failed),
// i.e. everything except still-in-flight (sending/queued). That is total - sending.
export const getManualTextProcessed = (task) =>
  Math.max(getManualTextTotal(task) - getManualTextSending(task), 0);

// Counts come from TaskFragment's server-side aggregates; see the alias definitions in
// src/graphql/tasks/queries.js. GET_TASK_DIVISIONS rows use their own aliases and are not read here.
export const countOf = (task, alias) => task?.[alias]?.aggregate?.count ?? 0;

export const getGoalCount = (task) => {
  if (task?.is_manual_number_task) {
    return getManualTextTotal(task);
  }
  return task?.tasks_sub_tasks?.length
    ? task.tasks_sub_tasks.reduce((acc, curr) => acc + (curr?.goal?.aggregate?.count ?? 0), 0)
    : countOf(task, 'votersTotal');
};

const getSubTaskCompleted = (task) =>
  task.tasks_sub_tasks.reduce((acc, curr) => acc + (curr?.completed?.aggregate?.count ?? 0), 0);

export const getCompletedCount = (task) => {
  if (task?.is_manual_number_task) {
    return getManualTextProcessed(task);
  }

  switch (task?.tasks_task_types?.[0]?.task_types_t_type?.t_type_id) {
    case TASK_TYPE_ID_MAP[TASK_TYPES.MESSAGE]:
      return countOf(task, 'smsFinishedOr');
    case TASK_TYPE_ID_MAP[TASK_TYPES.SMS_SURVEY]:
      return countOf(task, 'surveyNotFailed');
    case TASK_TYPE_ID_MAP[TASK_TYPES.VOICE_RECORDING]:
      return countOf(task, 'audioFinished');
    default:
      return task?.tasks_sub_tasks?.length
        ? getSubTaskCompleted(task)
        : countOf(task, 'votersCompleted');
  }
};

export const getCanceledDirectSndSmsCount = (task) => countOf(task, 'smsCanceled');

export const getSuccessfulCount = (task) => {
  if (task?.is_manual_number_task) {
    return getManualTextSent(task);
  }

  switch (task?.tasks_task_types?.[0]?.task_types_t_type?.t_type_id) {
    case TASK_TYPE_ID_MAP[TASK_TYPES.MESSAGE]:
      return countOf(task, 'smsSuccessfulOr');
    case TASK_TYPE_ID_MAP[TASK_TYPES.SMS_SURVEY]:
      return countOf(task, 'surveySuccessful');
    case TASK_TYPE_ID_MAP[TASK_TYPES.VOICE_RECORDING]:
      return countOf(task, 'audioSuccessful');
    default:
      return countOf(task, 'votersCompleted');
  }
};

/**
 * A voting area only carries state_id when it is state-scoped; a county-,
 * precinct- or district-scoped one records its geography in the matching column
 * instead and leaves the state null. Every one of those geographies sits inside
 * exactly one state, so the state is still derivable — ordered narrowest first,
 * since a disagreement between two rungs is a data fault rather than a choice.
 *
 * Mirrors backend/src/utils/voting-area-state.ts. Keep the two ladders in step.
 */
export const STATE_SOURCES = [
  { name: 'state', stateId: (a) => a.state_id },
  { name: 'precinct', stateId: (a) => a.voting_areas_precinct?.state_id },
  { name: 'city_council', stateId: (a) => a.voting_areas_city_council?.state_id },
  { name: 'city', stateId: (a) => a.voting_areas_city?.state_id },
  { name: 'county', stateId: (a) => a.voting_areas_county?.state_id },
  {
    name: 'state_leg_lower_sub_district',
    stateId: (a) => a.voting_areas_state_leg_lower_sub_district?.state_id,
  },
  {
    name: 'state_leg_lower_district',
    stateId: (a) => a.voting_areas_state_leg_lower_district?.state_id,
  },
  {
    name: 'state_leg_upper_district',
    stateId: (a) => a.voting_areas_state_leg_upper_district?.state_id,
  },
  {
    name: 'congressional_district',
    stateId: (a) => a.voting_areas_congressional_district?.state_id,
  },
];

/**
 * The first voting area that yields a known state wins. Scanning past an
 * unresolvable one matters: a task's list routinely mixes a state-bearing row
 * with narrower rows, in either order.
 */
export const resolveTaskState = (taskVotingAreas, states) => {
  const shortNameById = new Map((states ?? []).map((s) => [s.state_id, s.short_name]));

  for (const taskVotingArea of taskVotingAreas ?? []) {
    const votingArea = taskVotingArea?.task_voting_areas_voting_area;
    if (!votingArea) continue;

    for (const source of STATE_SOURCES) {
      const stateId = source.stateId(votingArea);
      if (stateId === null || stateId === undefined) continue;

      const shortName = shortNameById.get(stateId);
      // A state_id the states table does not know is unusable as a filter --
      // keep looking rather than handing a caller a name it cannot use.
      if (!shortName) continue;

      return {
        stateId,
        shortName,
        source: source.name,
        votingAreaId: votingArea.voting_area_id ?? null,
      };
    }
  }

  return null;
};
